import React, { useState } from 'react';
import FormSection from './FormSection';
import CapTableExcel from './CapTableExcel';

class FundingSection extends FormSection {
  constructor(props) {
    super(props);
    this.state = {
      capTableData: null,
      capTableTotals: null,
      capTableFlags: []
    };
  }

  validate() {
    const errors = [];
    const { watch } = this.props;
    
    const hasRaised = watch('hasRaised');
    
    if (!hasRaised) errors.push('Funding history is required');
    
    // Validate cap table if data exists
    if (this.state.capTableData) {
      const capTableErrors = this.validateCapTable();
      errors.push(...capTableErrors);
    }

    return errors;
  }

  validateCapTable() {
    const errors = [];
    const { capTableTotals } = this.state;
    
    if (!capTableTotals || capTableTotals.grandTotal === 0) {
      errors.push('Cap table must have at least some shares allocated');
    }
    
    return errors;
  }

  detectCapTableFlags(capTableData, totals) {
    const flags = [];
    
    if (!capTableData || !totals || totals.grandTotal === 0) {
      return flags;
    }

    const { founders, employees, investors, grandTotal } = totals;
    
    // Calculate percentages
    const founderPercentage = (founders / grandTotal) * 100;
    const employeePercentage = (employees / grandTotal) * 100;
    const investorPercentage = (investors / grandTotal) * 100;

    // Flag: Founders collectively own <70%
    if (founderPercentage < 70) {
      flags.push({
        type: 'yellow',
        message: 'Founder/s collectively own <70% → Indicates too much early dilution or misaligned incentives'
      });
    }

    // Flag: Single founder with no other team members and owns <80%
    if (capTableData.founders && capTableData.founders.length === 1 && founderPercentage < 80) {
      flags.push({
        type: 'yellow',
        message: 'Single founder with no other team members and owns <80% → Signals lack of team-building and outsized early dilution'
      });
    }

    // Flag: Advisor(s) or consultants with >5% equity
    // Check if any founders have "advisor" or "consultant" in their name/role
    const advisorShares = capTableData.founders ? capTableData.founders.filter(f => 
      f.name && (f.name.toLowerCase().includes('advisor') || f.name.toLowerCase().includes('consultant'))
    ).reduce((sum, advisor) => sum + advisor.shares, 0) : 0;
    
    const advisorPercentage = (advisorShares / grandTotal) * 100;
    if (advisorPercentage > 5) {
      flags.push({
        type: 'yellow',
        message: 'Advisor(s) or consultants with >5% equity → Often suggests poor judgment or desperation in early deals'
      });
    }

    // Flag: More than 5 investors already on CAP table
    const totalInvestors = capTableData.investors ? capTableData.investors.length : 0;
    if (totalInvestors > 5) {
      flags.push({
        type: 'yellow',
        message: 'More than 5 investors already on CAP table → Can signal poor fundraising hygiene and future cap table friction'
      });
    }

    // Flag: Co-founders with drastically uneven ownership
    if (capTableData.founders && capTableData.founders.length >= 2) {
      const founderShares = capTableData.founders.map(f => f.shares);
      const founderPercentages = founderShares.map(shares => (shares / grandTotal) * 100);
      const maxDiff = Math.max(...founderPercentages) - Math.min(...founderPercentages);
      
      if (capTableData.founders.length === 2 && maxDiff > 20) {
        flags.push({
          type: 'yellow',
          message: 'Co-founders with drastically uneven ownership (>20% difference) → Indicates potential future disputes or lack of trust/collaboration'
        });
      } else if (capTableData.founders.length >= 3 && maxDiff > 15) {
        flags.push({
          type: 'yellow',
          message: 'Co-founders with drastically uneven ownership (>15% difference) → Indicates potential future disputes or lack of trust/collaboration'
        });
      }
    }

    // Flag: Inactive or departed co-founder still owns meaningful equity
    // Check if any founder names suggest they're inactive
    const inactiveFounders = capTableData.founders ? capTableData.founders.filter(f => 
      f.name && (
        f.name.toLowerCase().includes('(inactive)') ||
        f.name.toLowerCase().includes('(departed)') ||
        f.name.toLowerCase().includes('former') ||
        f.name.toLowerCase().includes('ex-') ||
        f.name.toLowerCase().includes('previous')
      )
    ) : [];
    
    for (const founder of inactiveFounders) {
      const founderPercentage = (founder.shares / grandTotal) * 100;
      if (founderPercentage > 10) {
        flags.push({
          type: 'yellow',
          message: 'Inactive or departed co-founder still owns meaningful equity (>10%) → Potential deadweight or cap table headache'
        });
      }
    }

    return flags;
  }

  handleCapTableChange = (capTableData, totals) => {
    const flags = this.detectCapTableFlags(capTableData, totals);
    
    this.setState({
      capTableData,
      capTableTotals: totals,
      capTableFlags: flags
    });

    // Update form data
    if (this.props.setValue) {
      this.props.setValue('capTableData', capTableData);
      this.props.setValue('capTableTotals', totals);
      this.props.setValue('capTableFlags', flags);
    }
  };

  render() {
    const { watch } = this.props;
    const hasRaised = watch('hasRaised');
    const { capTableFlags } = this.state;

    return (
      <div className="border border-gray-200 rounded-lg p-6 mb-6">
        {this.renderSectionHeader('Part 3: Funding')}
        
        <div className="space-y-6">
          {/* Funding History */}
          <div>
            {this.renderField({
              name: 'hasRaised',
              label: 'Funding History: Have you raised any capital to date?',
              type: 'radio',
              required: true,
              options: [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]
            })}
            
            {/* Conditional field for funding details */}
            {this.renderConditionalField(
              { field: 'hasRaised', equals: 'yes' },
              {
                name: 'fundingDetails',
                label: 'What is the total amount of capital you have raised to date and on what terms?',
                type: 'textarea',
                required: true,
                placeholder: 'e.g., $150K on a post-money safe'
              }
            )}
          </div>

          {/* Cap Table */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Equity Breakdown</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a simple CAP table or equity breakdown. All percentages will be calculated automatically.
            </p>
            
            <CapTableExcel
              onChange={this.handleCapTableChange}
              initialData={this.state.capTableData}
            />
          </div>

          {/* Cap Table Flags */}
          {capTableFlags.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Cap Table Flags Detected
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc list-inside space-y-1">
                      {capTableFlags.map((flag, index) => (
                        <li key={index}>{flag.message}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-3 text-sm text-yellow-700">
                    <p className="font-medium">
                      These flags may require manual review during the application process.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Funding Context */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Context</h3>
            
            {this.renderField({
              name: 'fundingPlans',
              label: 'Future Funding Plans',
              type: 'textarea',
              required: false,
              placeholder: 'Describe your future funding plans, timeline, and use of funds (optional)'
            })}
          </div>
        </div>
      </div>
    );
  }
}

export default FundingSection;